import "katex/dist/katex.min.css";

import type { FC } from "react";
import { Fragment, useEffect, useState } from "react";
import Latex from "react-latex-next";

import { TutoringFooter } from "./components/TutoringFooter";
import { TutoringHeader } from "./components/TutoringHeader";

import { ABCDOptions, ErrorModal } from "components";
import AnswerTutoring from "components/Answer/AnswerTutoring";
import { FirstIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/FirstIncorrectAnswer";
import { SecondIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/SecondIncorrectAnswer";

import type { CheckTutoringAnswerMutation, UserAssignmentDetailFragment } from "api/generated";
import { useCheckTutoringAnswerMutation } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import type { IOption } from "store/slices/topicPractice";
import { processExerciseError } from "utils/processApiError";

import { getAssignmentTime, resetAssignmentTimer, startAssignmentTimer, stopAssignmentTimer } from "store/slices/timer";
import { ABCDOptionText } from "utils/formatTextForAnalysis";
import { getTimeToLearn } from "utils/getTimeToLearn";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";
import { useTutoringContext } from "./context/TutoringContext";

interface Props {
  data: UserAssignmentDetailFragment;
  userAssignmentId: string;
  onNextClick: () => void;
}

const ABCD: FC<Props> = ({ data, userAssignmentId, onNextClick }) => {
  const questionsCount = data.assignment?.assignment?.questions.length ?? 0;

  const dispatch = useAppDispatch();
  const [answers, setAnswers] = useState<IOption[]>(new Array(questionsCount).fill({}));
  const [apiResults, setApiResults] = useState<boolean[]>([]);
  const time = useAppSelector(getAssignmentTime);

  const [sendAnswers, { error: apiMutationError, isLoading: isSendingAnswers }] = useCheckTutoringAnswerMutation();

  const [submit, setSubmit] = useState(false);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { tutoringId, videoTips, claimEvaluation } = useTutoringContext();
  const [activeAccordion, setAccordionActive] = useState<string | string[] | undefined | null>(null);

  useEffect(() => {
    dispatch(resetAssignmentTimer());
    dispatch(startAssignmentTimer());

    return () => {
      dispatch(resetAssignmentTimer());
    };
  }, []);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processExerciseError(apiMutationError, {
      assignmentId: userAssignmentId,
      selfEvaluation: null,
      userAnswers: (answers ?? []).map(answer => answer.value ?? ""),
      timeToSolve: time,
    });
    setErrorModalVisible(true);
  }, [apiMutationError]);

  useEffect(() => {
    if (!submit) return;

    sendAnswers({
      tutoringId,
      userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers.map(answer => ({
        answers: [answer.value ?? ""],
      })),
      timeToSolve: time,
    })
      .then(response => {
        if ("data" in response && response.data) {
          setApiResults(response.data.tutoringAnswer.questionResults ?? []);
          processAnswerResponse(response);
          setErrorModalVisible(false);

          setAccordionActive(prev => {
            if (prev) return prev;
            const results = response.data.tutoringAnswer.questionResults ?? [];
            return results.length > 0 && !results.every(result => result) ? "0" : null;
          });
        }
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        dispatch(resetAssignmentTimer());
        dispatch(startAssignmentTimer());
        setSubmit(false);
      });
  }, [submit]);

  const processAnswerResponse = (response: { data: CheckTutoringAnswerMutation }) => {
    const { questionResults } = response.data.tutoringAnswer;

    if (!questionResults) return;

    const answersCopy = [...(answers ?? [])];
    questionResults.forEach((result, index) => {
      if (!result) answersCopy[index] = {};
    });

    setAnswers(answersCopy);
  };

  const onSubmit = () => {
    setSubmit(true);
    setAttempt(prev => prev + 1);
  };

  const handleNextClick = () => {
    dispatch(stopAssignmentTimer());
    onNextClick();
  };

  const handleCheck = (index: number, option: IOption) => {
    const copy = [...answers];
    copy[index] = option;
    setAnswers(copy);
  };

  useEffect(() => {
    const areAllCorrect = apiResults.length > 0 && apiResults.every(result => result);
    if (areAllCorrect) {
      claimEvaluation({ tutoringId, userAssignmentId, userAnswers: apiResults }).catch(() => {});
    }
  }, [answers]);

  const areAnswersFilled = () => answers.every(answer => answer.value && answer.value.length > 0);

  const toggleAccordion = (state: boolean) => {
    setAccordionActive(state ? "0" : null);
  };

  const videoIds =
    data.assignment?.analysis?.hasSharedAnalysis === true
      ? [data.assignment.analysis.sharedAnalysis?.explainerVideo].filter(BooleanWithTypeGuard)
      : [
          ...new Set(
            data.assignment?.assignment?.questions
              .map(question => question.solutionAnalysis?.explainerVideo)
              .filter(BooleanWithTypeGuard),
          ),
        ];

  return (
    <div className="exercise__body">
      <TutoringHeader
        data={data}
        userAssignmentId={userAssignmentId}
        activeAccordion={activeAccordion}
        toggleAccordion={toggleAccordion}
        videoIds={videoIds}
        videoTips={videoTips}
      />

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => {
          if (apiResults[qIndex]) {
            return (
              <Fragment key={`answer-${qIndex}`}>
                <strong>
                  <Latex>{question.heading ?? ""}</Latex>
                </strong>

                <AnswerTutoring
                  index={qIndex}
                  assignment={data.assignment}
                  type={apiResults?.[qIndex] ? "success" : "danger"}
                  userAssignmentId={data.id}
                  subject={data.subject?.appName}
                  timeToLearn={getTimeToLearn(data.timeToLearn)}
                >
                  {ABCDOptionText(answers[qIndex])}
                </AnswerTutoring>
              </Fragment>
            );
          }

          return (
            <div key={qIndex}>
              <Latex>{question.heading ?? ""}</Latex>
              <ABCDOptions
                options={question.options}
                qIndex={qIndex}
                value={answers[qIndex]}
                onClick={handleCheck}
                isAnswerCorrect={apiResults?.[qIndex]}
              />
            </div>
          );
        })}

        <ErrorModal
          onCancel={() => {
            setErrorModalVisible(false);
          }}
          onHide={() => {
            setErrorModalVisible(false);
          }}
          onRetry={() => {
            onSubmit();
          }}
          show={isErrorModalVisible}
        />

        {!isSendingAnswers && apiResults.includes(false) && (
          <>
            {!videoIds?.length || attempt === 1 ? (
              <FirstIncorrectAnswerModal
                incorrectCount={apiResults.filter(result => !result).length}
                questionsCount={questionsCount}
              />
            ) : null}

            {videoIds?.length && attempt > 1 ? (
              <SecondIncorrectAnswerModal videoIds={videoIds} attempt={attempt} />
            ) : null}
          </>
        )}

        <TutoringFooter
          onSubmit={onSubmit}
          isSubmitDisabled={!areAnswersFilled() || isSendingAnswers}
          isAnalysis={apiResults.length > 0 && apiResults.every(result => result)}
          goNext={handleNextClick}
        />
      </div>
    </div>
  );
};

export default ABCD;
